import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

export const PROPOSAL_THANKS_COPY = {
  lines: [
    "Thank you for taking the time to review.",
    "I look forward to continuing this walk with you.",
    "Your invoice is unlocked — swipe to the next page when you’re ready.",
  ],
  heart: "♡",
  signOff: "Be well.",
};

export const BARTER_THANKS_COPY = {
  lines: [
    "Thank you for taking the time. I've signed the partnership as well, and a copy should be on its way to you.",
    "I'm genuinely grateful to be building alongside you. From here, we'll shape the first month's scope and get to work.",
  ],
  heart: "<3",
  signOff: "Let's continue to be great.",
};

/** "Jules Ortega" → "Jules," */
export function formatThanksGreeting(name) {
  const first = String(name || "")
    .trim()
    .split(/\s+/)[0];
  if (!first) return "";
  return first.endsWith(",") ? first : `${first},`;
}

function thanksHeadline(optionLabel, clientName) {
  return [optionLabel, clientName].filter(Boolean).join(" · ");
}

function useCursorWash() {
  const targetRef = useRef({ x: 50, y: 28 });
  const currentRef = useRef({ x: 50, y: 28 });
  const frameRef = useRef(null);
  const [pos, setPos] = useState({ x: 50, y: 28 });
  const [track, setTrack] = useState(false);

  useEffect(() => {
    const fine = window.matchMedia("(pointer: fine)").matches;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!fine || reduce) return undefined;
    setTrack(true);
    const tick = () => {
      const t = targetRef.current;
      const c = currentRef.current;
      const next = {
        x: c.x + (t.x - c.x) * 0.055,
        y: c.y + (t.y - c.y) * 0.055,
      };
      currentRef.current = next;
      setPos(next);
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, []);

  const onPointerMove = (e) => {
    if (!track) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    targetRef.current = {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    };
  };

  return { pos, onPointerMove };
}

function ThanksBody({ className = "", copy = PROPOSAL_THANKS_COPY }) {
  return (
    <div className={className}>
      {copy.lines.map((line) => (
        <p key={line} className="m-0">
          {line}
        </p>
      ))}
      <p className="m-0 pt-1 text-[var(--t-accent)]" aria-hidden>
        {copy.heart}
      </p>
      <p className="m-0 pt-5 font-heading text-xl tracking-tight !font-extralight text-[var(--t-text)]">
        {copy.signOff}
      </p>
    </div>
  );
}

/**
 * Post-accept thank-you — sealed wash + seal card (final).
 * Greeting is always first name with a trailing comma, e.g. "Jules,"
 */
export default function ProposalThanksScreen({
  clientName = "",
  optionLabel = "",
  personName = "",
  variant = "proposal",
}) {
  const { pos, onPointerMove } = useCursorWash();
  const reduce = useReducedMotion();
  const headline = thanksHeadline(optionLabel, clientName);
  const greeting = formatThanksGreeting(personName);
  const copy = variant === "barter" ? BARTER_THANKS_COPY : PROPOSAL_THANKS_COPY;

  return (
    <div
      className="login-sealed pointer-events-auto fixed inset-0 z-0 flex flex-col overflow-hidden px-5 pb-6 pt-10 md:px-12 md:pb-10 md:pt-14"
      data-testid="proposal-thanks-page"
      data-variant={variant}
      onPointerMove={onPointerMove}
      style={{
        "--wash-x": `${pos.x}%`,
        "--wash-y": `${pos.y}%`,
      }}
    >
      <div className="login-sealed__wash" aria-hidden />
      <div className="login-sealed__aura-soft" aria-hidden />
      <div className="login-sealed__aura" aria-hidden />

      <div className="relative mx-auto flex min-h-0 w-full max-w-[440px] flex-1 flex-col justify-center">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="login-lab-hybrid__seal w-full border border-[var(--t-border)] bg-[rgba(252,252,252,0.92)] p-7 md:p-9"
          data-testid="proposal-thanks-card"
        >
          {headline ? (
            <div
              className={`label-eyebrow text-[var(--t-accent)] ${greeting ? "mb-3" : "mb-6"}`}
              data-testid="proposal-thanks-headline"
            >
              {headline}
            </div>
          ) : null}
          {greeting ? (
            <p
              className="mb-6 font-heading text-xl tracking-tight !font-extralight text-[var(--t-text)]"
              data-testid="proposal-thanks-person"
            >
              {greeting}
            </p>
          ) : null}
          <ThanksBody className="space-y-3 text-[15px] leading-relaxed text-[var(--t-text)]" copy={copy} />
        </motion.div>
      </div>

      <p className="relative z-10 mt-8 text-center text-[11px] tracking-[0.08em] text-[var(--t-meta)]">
        <a href="/privacy" className="hover:text-[var(--t-accent)]">
          Privacy Policy
        </a>
      </p>
    </div>
  );
}
